import { getGameByInviteCode, getGameFromSlug } from "../../../api/user";
import { leagueEndAt, leagueStartAt } from "./leagueFormat";

// Private league invites.
//
// THE SLUG AND THE INVITE CODE ARE TWO DIFFERENT THINGS:
//
//   slug        games.vanity_url — "carter-investing-club". The league's URL
//               identifier, and NOTHING more. Knowing it grants no access, which
//               is the whole point: the league name is not the password.
//   invite code games.invite_code — "CARTER25". A separate credential QUSHi
//               generates for private leagues (6 title letters + 2 digits) and
//               the organiser hands out, by link or as something typeable.
//
// So a private league can be reached two ways, both landing on the same preview:
//   1. the invite link, /join/<INVITE_CODE> — validated on open, nothing to type
//   2. the code typed into the modal on League Play
//
// Anyone arriving with only the slug gets the invite gate instead of the league.
// Membership, once granted, replaces the code permanently.

export const INVITE_ERRORS = {
  notFound: "Code not found. Check with your league creator.",
  started: "This league has already started.",
  expired: "This invite is no longer active.",
  offline: "We couldn't reach Qushi. Check your connection and try again.",
};

// What the backend generates: letters then digits, no separators. Used to decide
// whether a URL segment is a code or a slug — a slug is lowercase and hyphenated,
// a code is neither.
const INVITE_CODE_RE = /^[A-Z0-9]{4,16}$/;

/**
 * What the user typed or pasted → the invite code to look up.
 *
 * Handles a pasted invite link as well as a typed code, strips ALL whitespace
 * rather than just the ends (a code copied out of a chat often carries a stray
 * space or newline), and upper-cases, since the codes are.
 */
export const normalizeInviteCode = (raw) => {
  const value = (raw || "").replace(/\s+/g, "");
  if (!value) return "";

  // Pull the credential out of an invite link.
  const fromUrl = value.match(/\/(?:join|leagues|contest)\/([^/?#]+)/i);
  const candidate = fromUrl ? fromUrl[1] : value;

  // A bare host with nothing useful after it isn't a code.
  if (!fromUrl && (candidate.includes("://") || candidate.includes("."))) return "";

  return candidate.toUpperCase();
};

export const looksLikeInviteCode = (value) => INVITE_CODE_RE.test(value || "");

/**
 * Can this user still join? Returns an INVITE_ERRORS message, or null.
 *
 * Members are never gated — someone already in the league has to be able to get
 * back to it whatever the dates say.
 */
export const inviteBlocker = (game, now = new Date()) => {
  if (!game || game.isMember) return null;

  if (game.status === "completed" || game.status === "cancelled") return INVITE_ERRORS.expired;

  // WHEN the league actually opens and closes, not the two bare dates stored
  // against it.
  //
  // Client: "The invite code feature is not working for private league sign-up.
  // Gives an error message. As the organizer, the invite code will work. But
  // when a member tries to use the code to find the landing page, it shows an
  // error."
  //
  // The organiser was never the exception — they are a MEMBER, and the very
  // first line above lets a member straight through. Everyone else fell into
  // this test, which read `end_date` as an instant. `end_date` is a plain DATE
  // stored at midnight OF the final day, so from 00:00 on its last day
  // `end < now` was true and a league that was still running for another twelve
  // hours told every invitee "This invite is no longer active." The screenshot
  // the client sent shows exactly that: the league's own header still counting
  // down "Ends in 4h" behind the refused code.
  //
  // `start_date` had the same defect on the other end. A league that does not
  // allow late entry was declared "already started" from midnight, eight hours
  // before its 8 AM ET open — so an invitee on the morning of day one was locked
  // out of a league nobody had started trading in yet.
  //
  // leagueStartAt / leagueEndAt are the app's single answer to both questions —
  // they resolve a stored date to the league's real 8 AM / 8 PM ET boundary and
  // they are what the countdown, the status pill
  // and the landing pages already read. This file was the last place still doing
  // its own `new Date(...)` on these two fields, which is why the gate could
  // disagree with the countdown printed directly above it.
  const end = leagueEndAt(game);
  if (end && end < now) return INVITE_ERRORS.expired;

  if (isEntryClosed(game, now)) return INVITE_ERRORS.started;

  return null;
};

/**
 * "Has this competition stopped taking new entrants?"
 *
 * Client: "Once a game starts, no one should be able to enter the game other
 * than members, which can easily enter from their My Leagues... I will always
 * choose not to allow joins after start in the creation screen. Make sure this
 * feature works for all leagues and games. Right now, it allows me to join even
 * after the game has started."
 *
 * `join_after_start` was written by both create wizards and then read back for
 * DISPLAY only — "Open to join" / "Entry closed" on the cards, and the invite
 * gate above. Every actual Join / Enter Free button ignored it, so the setting
 * did nothing on the one screen where it was supposed to bite.
 *
 * Pulled out of inviteBlocker rather than copied beside it, so the invite path
 * and the button paths cannot start disagreeing about when entry shuts. The
 * server is still the authority (GameController::joinGame returns
 * GAME_ENTRY_CLOSED); this is what stops the button being offered in the first
 * place.
 *
 * A member is never gated — they are already in.
 */
export const isEntryClosed = (game, now = new Date()) => {
  if (!game || game.isMember) return false;
  const start = leagueStartAt(game);
  return !!start && start <= now && !game.join_after_start;
};

// Both league endpoints answer a miss with HTTP 200 and `success: false` (see
// PublicException::render), so a failed lookup never throws — it has to be read
// off the body.
const readGame = (res) => {
  const body = res?.data;
  if (!body || body.success === false) return null;
  const game = body.data;
  return game && game.id ? game : null;
};

const byCode = async (code) => {
  try {
    return readGame(await getGameByInviteCode({ code }));
  } catch (e) {
    // 404 is the documented "no such code" answer from this endpoint.
    if (e?.response?.status === 404) return null;
    throw e;
  }
};

const bySlug = async (slug) => {
  try {
    return readGame(await getGameFromSlug({ slug }));
  } catch (e) {
    if (e?.response) return null;
    throw e;
  }
};

/**
 * Resolve an invite CODE — the modal's job, and the invite link's.
 *
 * Returns { ok: true, game, via: "code" } or { ok: false, message }. The message
 * is ready to show inline, never as a redirect.
 */
export const resolveInviteCode = async (rawCode) => {
  const code = normalizeInviteCode(rawCode);
  if (!code) return { ok: false, message: INVITE_ERRORS.notFound };

  let game = null;
  try {
    game = await byCode(code);
  } catch (e) {
    console.error("Invite code lookup failed:", e);
    return { ok: false, message: INVITE_ERRORS.offline };
  }

  if (!game) return { ok: false, message: INVITE_ERRORS.notFound };

  const blocker = inviteBlocker(game);
  if (blocker) return { ok: false, message: blocker, game };

  return { ok: true, game, via: "code", code };
};

/**
 * Resolve whatever is in an invite URL, which may be either credential.
 *
 * `via` is what the caller needs to decide whether to show the league at all:
 * "code" is a validated invite, "slug" is just an address — and an address alone
 * only opens the league for someone who is already a member.
 */
export const resolveInviteToken = async (token) => {
  const value = normalizeInviteCode(token);
  if (!value) return { ok: false, message: INVITE_ERRORS.notFound };

  if (looksLikeInviteCode(value)) {
    const byInvite = await resolveInviteCode(value);
    // A hyphen-free lowercase slug can also match the code shape, so fall through
    // to the slug lookup rather than reporting "not found" on a real league.
    if (byInvite.ok || byInvite.game) return byInvite;
  }

  let game = null;
  try {
    game = await bySlug(String(token).trim());
  } catch (e) {
    console.error("Invite slug lookup failed:", e);
    return { ok: false, message: INVITE_ERRORS.offline };
  }

  if (!game) return { ok: false, message: INVITE_ERRORS.notFound };

  const blocker = inviteBlocker(game);
  if (blocker) return { ok: false, message: blocker, game };

  return { ok: true, game, via: "slug" };
};

/**
 * Is this viewer allowed to see this private league's preview?
 *
 * A validated invite code, or existing membership. The slug on its own is not
 * access — that's the security improvement this model exists for.
 */
export const canPreview = (game, via) =>
  !!game && (game.game_type !== "private" || via === "code" || !!game.isMember);

// GameController@getGameFromSlug returns the members as a relation rather than a
// count, so derive one for the screens that show "N members".
export const memberCount = (game) => {
  if (!game) return 0;
  if (Number.isFinite(Number(game.members_count))) return Number(game.members_count);
  if (Array.isArray(game.game_members)) return game.game_members.length;
  if (Array.isArray(game.members)) return game.members.length;
  return 0;
};
